import React from "react";
import PropTypes from "prop-types";
import styled from "styled-components";

// context
import { useSiteHeader } from "core/SiteHeader/context/useSiteHeader.js";

// components
import OpenModalButton from "./OpenModalButton";

// styles
import { convertPxToRem } from "frontend/components/helpers";
import colors from "core/colors";
import { regular } from "core/fontStyles";

const TextStyled = styled.div`
    font-size: ${convertPxToRem(15)};
    ${regular};
    white-space: nowrap;
    color: ${({ $isDarkMode }) =>
        $isDarkMode ? colors.primary.diamond : colors.secondary.orca};
    overflow: hidden;
    text-overflow: ellipsis;
    overflow-wrap: break-word;

    // Used to calculate truncation width:
    // 330px min - 8px dot - 6px gap ≈ 316px
    // Buffer for non-monospace font → 300px
    max-width: ${convertPxToRem(300)};
`;

// Converts string from backend into Button and Text regardless of where the button shows up in the string.
// Examples:
// {{Promotion Available}} - Get 20% off Creator Pro
// Check out our new {{Icon Collections}}
// {{Promotion Available}} - Get 20% {{off Creator}} Pro
// This will even work with non-adjacent bracketed text - e.g. more than one button in the string.
// I do not see why we would need multiple buttons, just pointing out it is flexible enough
const parseMarketingMessageString = (message, onOpenModal) => {
    if (!message) return null;

    const regex = /{{(.*?)}}/g;
    const parts = []; // array to capture final parsed message
    let lastIndex = 0; // keeps track of the parsing position in the original string
    let match;

    while ((match = regex.exec(message)) !== null) {
        const { index } = match;
        const matchText = match[1];

        // Push text before match
        if (index > lastIndex) {
            parts.push(message.slice(lastIndex, index));
        }

        // Push button for matched token
        parts.push(
            <OpenModalButton key={index} value={`open-marketing-modal`} onClick={onOpenModal}>
                {matchText}
            </OpenModalButton>
        );

        lastIndex = regex.lastIndex;
    }

    // Push remaining text
    if (lastIndex < message.length) {
        parts.push(message.slice(lastIndex));
    }

    return parts;
};

const getPlainMarketingMessage = message => {
    if (typeof message !== "string" || !message) {
        return "";
    }
    return message.replace(/{{(.+)}}/g, "$1");
};

// component
export const Text = ({ message, onOpenModal }) => {
    const { isDarkMode } = useSiteHeader();
    const parsedMessage = parseMarketingMessageString(message, onOpenModal);
    const plainTextMessage = getPlainMarketingMessage(message);

    return (
        <TextStyled $isDarkMode={isDarkMode} title={plainTextMessage}>
            {parsedMessage}
        </TextStyled>
    );
};

export default Text;

// prop-types
Text.propTypes = {
    message: PropTypes.string.isRequired,
    onOpenModal: PropTypes.func.isRequired
};
